import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ShopMembersTab from './ShopMembersTab';
import LocationsTab from './LocationsTab';
import ShopDetailsTab from './ShopDetailsTab';
import SubscriptionManager from './SubscriptionManager';
import CollectionsTab from './CollectionsTab';
import InventoryImportTab from './InventoryImportTab';
import { useShop } from '../contexts/ShopContext';

interface ShopAdminTabProps {
  shopId: string;
}

type SubTabType = 'collections' | 'members' | 'locations' | 'subscription' | 'shop' | 'importCsv';

const ShopAdminTab: React.FC<ShopAdminTabProps> = ({ shopId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canManageShop, switchShop, currentShop } = useShop();

  // Parse hash path and query params: #shopAdmin/subscription?shopId=xxx
  const parseHash = (hash: string) => {
    const cleaned = hash.replace('#', '');
    const [path, queryString] = cleaned.split('?');
    const params = queryString ? new URLSearchParams(queryString) : null;
    return { path, params };
  };

  // Extract sub-tab from hash path
  const getSubTabFromHash = (hash: string): SubTabType | null => {
    const { path } = parseHash(hash);
    const parts = path.split('/');
    if (parts.length > 1 && parts[0] === 'shopAdmin') {
      const subTab = parts[1];
      if (subTab === 'collections' || subTab === 'members' || subTab === 'locations'
          || subTab === 'subscription' || subTab === 'shop' || subTab === 'importCsv') {
        return subTab;
      }
    }
    return null;
  };

  // Admins land on Members (existing behavior); non-admins (MEMBER/VIEWER) only
  // see Collections, so that's their default.
  const defaultSubTab: SubTabType = canManageShop ? 'members' : 'collections';

  const [activeSubTab, setActiveSubTab] = useState<SubTabType>(
    getSubTabFromHash(location.hash) || defaultSubTab
  );

  // Update sub-tab when URL hash changes
  useEffect(() => {
    const subTab = getSubTabFromHash(location.hash);
    if (subTab) {
      setActiveSubTab(subTab);
    }
  }, [location.hash]);

  // Switch to the shop specified in the URL query param (e.g., after Stripe checkout redirect)
  useEffect(() => {
    const { params } = parseHash(location.hash);
    const urlShopId = params?.get('shopId');
    if (urlShopId && currentShop && urlShopId !== currentShop.id) {
      switchShop(urlShopId);
      // Clean up the query param from the URL
      navigate(`/#shopAdmin/${activeSubTab}`, { replace: true });
    }
  }, [location.hash, currentShop]);

  // Handle sub-tab change - update both state and URL
  const handleSubTabChange = (subTab: SubTabType) => {
    setActiveSubTab(subTab);
    navigate(`/#shopAdmin/${subTab}`, { replace: true });
  };

  // Build sub-tabs array based on permissions. Collections is available to all
  // internal members; the rest are admin-only (canManageShop).
  const subTabs = [
    { id: 'collections' as SubTabType, label: 'Collections' },
    ...(canManageShop ? [{ id: 'members' as SubTabType, label: 'Members' }] : []),
    ...(canManageShop ? [{ id: 'locations' as SubTabType, label: 'Locations' }] : []),
    ...(canManageShop ? [{ id: 'subscription' as SubTabType, label: 'Subscription' }] : []),
    ...(canManageShop ? [{ id: 'shop' as SubTabType, label: 'Settings' }] : []),
    ...(canManageShop ? [{ id: 'importCsv' as SubTabType, label: 'Import CSV' }] : [])
  ];

  return (
    <div className="shop-admin-tab">
      {/* Hide scrollbar for webkit browsers */}
      <style>{`
        .shop-admin-subtabs::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {/* Sub-tab Navigation */}
      <div style={{ marginBottom: '24px' }}>
        <nav
          className="shop-admin-subtabs"
          style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: 'var(--bg-secondary)',
            padding: '4px',
            borderRadius: '8px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {subTabs.map((subTab) => (
            <button
              key={subTab.id}
              onClick={() => handleSubTabChange(subTab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: activeSubTab === subTab.id ? 'var(--primary-light)' : 'transparent',
                color: activeSubTab === subTab.id ? 'white' : 'var(--text-primary)',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.15s ease',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeSubTab !== subTab.id) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSubTab !== subTab.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {subTab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-tab Content */}
      <div>
        {activeSubTab === 'collections' && (
          <CollectionsTab shopId={shopId} />
        )}
        {activeSubTab === 'members' && canManageShop && (
          <ShopMembersTab shopId={shopId} />
        )}
        {activeSubTab === 'locations' && canManageShop && (
          <LocationsTab shopId={shopId} />
        )}
        {activeSubTab === 'subscription' && canManageShop && (
          <SubscriptionManager shopId={shopId} />
        )}
        {activeSubTab === 'shop' && canManageShop && (
          <ShopDetailsTab shopId={shopId} />
        )}
        {activeSubTab === 'importCsv' && canManageShop && (
          <InventoryImportTab shopId={shopId} />
        )}
      </div>
    </div>
  );
};

export default ShopAdminTab;
